import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useProject } from "../../context/ProjectContext";
import AssignAgentComponent from "../../pages/Agents/AssignAgentComponent";
import { Button } from "@material-tailwind/react";
import usePrivilege from "../../hooks/usePrivilege";
import {
  fetchPropertyConfigurations,
  getPropertyConfigFormdata,
} from "../../services/projects";
import axiosInstance from "../../services/api/axios";
import { assignAgentsToConfigs } from "../../services/users";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useUser } from "../../context/UserContext";
import { Delete, Edit, Plus, Trash2 } from "lucide-react";
import CreateConfigModal from "./CreateConfigModal";
import { OutlineButton, PrimaryButton, IconButton } from "../Buttons";
import ImageCarousel from "../ImageCarousel";
import { normalizePropertyConfigMediaForUI } from "../../utils/media";


const ConfigDetails = ({ project, configToGo }) => {
  const { projectId } = useParams();
  const { hasPrivilege } = usePrivilege();
  const [loading, setLoading] = useState(true);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [Agents, setAgents] = useState([]);
  const [mediaImages, setMediaImages] = useState([]);
  const [detailedFloorPlansMedia, setDetailedFloorPlansMedia] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [assignedAgents, setassignedAgents] = useState([]);
  const [isEditing, setIsEditing] = useState(false); //No longer directly used for inline editing
  const [editConfig, setEditConfig] = useState(null);
  const { userInfo } = useUser();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const [refreshConfigurations, setRefreshConfigurations] = useState(false);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [previewNewImages, setPreviewNewImages] = useState([]);
  const [newImages, setNewImages] = useState([]);

  const propertyTypes = ["Apartment", "Shop", "Land", "Independent"]; // Define available property types

  const fetchImages = async (selectedConfigResponse) => {
    if (selectedConfigResponse) {
      try {
        const params = { property_config_id: selectedConfigResponse };
        const customerId = userInfo?.loginIdentifierDetails?.customer?.customerId;
        if (customerId) {
          params.customerId = customerId;
        }
        const response = await axiosInstance.get("/property/config/media", {
          params,
        });
        const { gallery, detailedFloorPlans } =
          normalizePropertyConfigMediaForUI(response);
        setMediaImages(gallery);
        setDetailedFloorPlansMedia(detailedFloorPlans);
      } catch (error) {
        console.error("Error fetching media images:", error);
        setMediaImages([]);
        setDetailedFloorPlansMedia([]);
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchAgents = async (forConfig) => {
    try {
      const allAgentsResponse = await axiosInstance.get(
        `customers/relation?projectId=${projectId}`
      );
      setAgents(allAgentsResponse);
      if (forConfig) {
        const selectAgentsResponse = await axiosInstance.get(
          `customers/property/config?propertyConfigId=${forConfig}`
        );
        setassignedAgents(selectAgentsResponse);
      }
    } catch (error) {
      console.error("Error fetching media images:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchBuildings = async () => {
    try {
      const response = await axiosInstance.get(
        `/projects/buildings?projectId=${projectId}`
      );
      setBuildings(response);
    } catch (error) {
      console.error("Error fetching buildings:", error);
    }
  };

  const loadConfigurations = async () => {
    setLoading(true);
    try {
      const response = await fetchPropertyConfigurations(
        projectId,
        userInfo.loginIdentifierDetails.customer.customerId
      );

      setConfigurations(response);

      let initialSelectedConfig = response[0];
      if (configToGo) {
        initialSelectedConfig = response.filter(
          (i) => i.propertyConfigId == configToGo
        )[0];
      }
      setSelectedConfig(initialSelectedConfig);
      setEditConfig({ ...initialSelectedConfig });
      if (initialSelectedConfig) {
        await fetchImages(initialSelectedConfig?.propertyConfigId);
        await fetchAgents(initialSelectedConfig?.propertyConfigId);
      }
      if (buildings.length === 0) {
        await fetchBuildings();
      }

      if (initialSelectedConfig && buildings.length > 0) {
        const building = buildings.find(
          (building) => building.buildingId === initialSelectedConfig.building.buildingId
        );
        setSelectedBuilding(building);
      }
    } catch (error) {
      console.error("Error loading configurations:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadConfigurations();
  }, [project, projectId, configToGo, refreshConfigurations]);

  const handleAddConfig = () => {
    setIsModalOpen(true);
    setEditConfig(null); // Ensure no edit config is passed
  };

  const handleEditClick = (selectedConfig) => {
    //setId(selectedConfig.propertyConfigId);
    setIsModalOpen(true);
    setEditConfig(selectedConfig); // Set selected config for editing
  };

  const handleCancelEdit = () => {
    setIsModalOpen(false);
    setEditConfig(null);
  };

  const handleInputChange = (e) => {
    e.stopPropagation();
    const { name, value } = e.target;
    setEditConfig({ ...editConfig, [name]: value });
  };
  const handleNewImageRemove = (index) => {
    const newImages = [...newImages];
    const newPreviews = [...previewNewImages];
    URL.revokeObjectURL(previewNewImages[index]);
    newImages.splice(index, 1);
    newPreviews.splice(index, 1);
    setNewImages(newImages);
    setPreviewNewImages(newPreviews);
  };
  const handleSave = async () => {
    try {
      if (!editConfig) {
        console.error("No configuration data to save.");
        return;
      }
      const formData = getPropertyConfigFormdata(editConfig);

      if (selectedConfig && selectedConfig.propertyConfigId) {
        try {
          await axiosInstance.put(
            `/property/config?propertyConfigId=${editConfig.propertyConfigId}`,
            formData,
            {
              headers: { "Content-Type": "multipart/form-data" },
            }
          );
          toast.success("Config details updated successfully");
          setRefreshConfigurations((prev) => !prev);
        } catch (error) {
          console.error("Error in executepropertyConfigForm:", error);
          toast.error("Failed to update config details");
        }

        setIsModalOpen(false);
      } else {
        console.error("Selected config or its ID is undefined");
      }
    } catch (error) {
      console.error("Error updating configuration:", error);
      toast.error("Failed to update config details");
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this configuration?")) {
      setConfigurations((prev) => prev.filter((config) => config.id !== id));
  
    }
  };

  const handleConfigChange = async (e) => {
    e.stopPropagation();
    const selectedId = parseInt(e.target.value);
    const config = configurations.find(
      (c) => c.propertyConfigId === selectedId
    );
    setSelectedConfig(config);
    setEditConfig({ ...config });
    await fetchImages(selectedId, "input select");
    await fetchAgents(config.propertyConfigId);
    if (buildings.length > 0) {
      const building = buildings.find(
        (building) => building.buildingId === config.buildingId
      );
      setSelectedBuilding(building);
    }
  };
  const handleAssignAgent = async (selectedAgent) => {
    const payload = selectedAgent.map((agent) => ({
      customerId: agent.secondaryCustomer.customerId,
      propertyConfigId: selectedConfig.propertyConfigId,
      isPrimary: false,
    }));
    if (selectedAgent && selectedConfig.propertyConfigId) {
      try {
        await assignAgentsToConfigs(payload);
        await fetchAgents(selectedConfig.propertyConfigId);
      } catch (error) {
        console.error("Error assigning agent:", error);
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditConfig(null); // Reset edit config on modal close
  };

  const handleConfigCreated = (newConfig) => {
    setRefreshConfigurations((prev) => !prev);
    setIsModalOpen(false);
    setEditConfig(null); //reset edit config on creation
  };

  const handleBuildingChange = async (e) => {
    e.stopPropagation();
    const selectedBuildingId = parseInt(e.target.value);
    const building = buildings.find((c) => c.buildingId === selectedBuildingId);
    setSelectedBuilding(building);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-base font-medium text-blue-600">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600 mx-auto mb-3"></div>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-100 mx-auto px-4 sm:px-6 lg:px-8 min-h-screen">
      <CreateConfigModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        projectId={projectId}
        onCreate={handleConfigCreated}
        editConfigData={editConfig} // Pass the selected configuration
      />
      <div className="flex flex-col sm:flex-row justify-between items-start mb-6">

        <div className="flex items-center mt-4"> {/*  flex container */}
          <div className="flex flex-col mr-4">
            {/* Select Configuration Section */}
            <label
              htmlFor="configSelect"
              className="block text-sm  font-medium text-gray-700 mb-1"
            >
              Select Configuration
            </label>
            <select
              id="configSelect"
              onChange={handleConfigChange}
              value={selectedConfig?.propertyConfigId || ""}
              className="block w-full sm:w-auto rounded-md border border-gray-200 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-200 text-sm py-2 px-3"
              style={{ fontSize: "1rem", padding: "0.75rem", minWidth: "450px", height: "45px", }}
            >
              {configurations.map((config) => (
                <option
                  key={config.propertyConfigId}
                  value={config.propertyConfigId}
                >
                  {config.configName}
                </option>
              ))}
            </select>
          </div>
          {/* Add Config Button Section */}
          {hasPrivilege("canEdit") && (
            <div className="ml-1 self-end">  {/* Add some left margin, align to the bottom */}
              <IconButton
                onClick={handleAddConfig}
                icon={<Plus className="mr-2 size-4" />}
                left
                className={"h-10"}
              >
                Add Config
              </IconButton>
            </div>
          )}
        </div>{/* end flex  container */}
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1 bg-white rounded-lg shadow-sm overflow-hidden">
          {selectedConfig ? (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedConfig.configName}
                </h3>

                {(hasPrivilege("canEdit") || hasPrivilege("canDelete")) && (
                  <div className="flex gap-3">
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditClick(selectedConfig)}
                        color="black"
                        className="p-2 "
                      >
                        <Edit size={17.5} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(selectedConfig.propertyConfigId)}
                        color="red"
                        className="px-2 py-1 bg-red-500 text-white hover:bg-red-600 border-none"
                      >
                        <Trash2 size={17.5} />
                      </Button>
                    </>
                  </div>
                )}
              </div>

              <div className="relative mb-8  overflow-hidden ">
                <ImageCarousel
                  images={mediaImages}
                  newImages={previewNewImages}
                  isEditing={isEditing}
                  handleImageRemove={handleNewImageRemove}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 rounded-lg">
                <div className="space-y-4">
                  <div className=" p-3 ">
                    <p className="flex justify-between items-center text-sm">
                      <span className="font-medium text-gray-700">Type:</span>
                      <span className="px-2 py-0.5 rounded-full">
                        {selectedConfig.propertyType}
                      </span>
                    </p>
                  </div>
                  <div className=" p-3 ">
                    <p className="flex justify-between items-center text-sm">
                      <span className="font-medium text-gray-700">
                        Building:
                      </span>
                      <span className="px-2 py-0.5 rounded-full">
                        {selectedConfig.building?.buildingName}
                      </span>
                    </p>
                  </div>
                  {selectedConfig.propertyType !== "Shop" && (
                    <div className=" p-3 ">
                      <p className="flex justify-between items-center text-sm">
                        <span className="font-medium text-gray-700">
                          Size:
                        </span>
                        <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                          {selectedConfig.sizeBhk}
                          BHK
                        </span>
                      </p>
                    </div>
                  )}
                  <div className=" p-3 ">
                    <p className="flex justify-between items-center text-sm">
                      <span className="font-medium text-gray-700">
                        Carpet Area:
                      </span>
                      <span className="text-gray-900">
                        {`${selectedConfig.carpetAreaSqmt} sq mt / ${selectedConfig.carpetAreaSqft} sq ft`}
                      </span>
                    </p>
                  </div>
                  {selectedConfig.propertyType !== "Shop" && (
                    <div className=" p-3 ">
                      <p className="flex justify-between items-center text-sm">
                        <span className="font-medium text-gray-700">
                          Dry Balcony Area:
                        </span>
                        <span className="text-gray-900">
                          {`${selectedConfig.dryBalconyAreaSqmt} sq mt / ${selectedConfig.dryBalconyAreaSqft} sq ft`}
                        </span>
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className=" p-3 ">
                    <p className="flex justify-between items-center text-sm">
                      <span className="font-medium text-gray-700">
                        Super Built-up Area:
                      </span>
                      <span className="text-gray-900">
                        {`${selectedConfig.superBuiltupAreaSqmt} sq mt / ${selectedConfig.superBuiltupAreaSqft} sq ft`}
                      </span>
                    </p>
                  </div>
                  {selectedConfig.propertyType !== "Shop" && (
                    <div className=" p-3 ">
                      <p className="flex justify-between items-center text-sm">
                        <span className="font-medium text-gray-700">
                          Balcony Area:
                        </span>
                        <span className="text-gray-900">
                          {`${selectedConfig.balconyAreaSqmt} sq mt / ${selectedConfig.balconyAreaSqft} sq ft`}
                        </span>
                      </p>
                    </div>
                  )}
                  <div className=" p-4 ">
                    <p className="flex justify-between items-center">
                      <span className="font-medium text-gray-700 text-sm">
                        Base Price:
                      </span>
                      <span className="text-gray-900">
                        {`₹${selectedConfig.basePrice.toLocaleString()}`}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {detailedFloorPlansMedia && detailedFloorPlansMedia.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200 px-6 pb-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">
                    Detailed floor plans
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {detailedFloorPlansMedia.map((floorPlan, index) => (
                      <div
                        key={floorPlan.url || `floor-${index}`}
                        className="relative border border-gray-200 rounded-lg overflow-hidden bg-gray-50 hover:shadow-md transition-shadow"
                      >
                        {floorPlan.contentType === "application/pdf" ? (
                          <a
                            href={floorPlan.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex p-4 h-36 items-center justify-center"
                          >
                            <div className="text-center">
                              <svg
                                className="w-12 h-12 mx-auto mb-2 text-red-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                />
                              </svg>
                              <span className="text-xs text-gray-600">PDF</span>
                            </div>
                          </a>
                        ) : (
                          <a
                            href={floorPlan.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img
                              src={floorPlan.url}
                              alt={`Floor plan ${index + 1}`}
                              className="w-full h-36 object-cover cursor-pointer"
                            />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6 text-center">
              <div className="max-w-md mx-auto">
                <svg
                  className="w-12 h-12 text-gray-400 mx-auto mb-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 12h.01M12 12h.01M12 12h.01M12 12h.01M12 12h.01M12 12h.01M12 12h.01M12 12h.01"
                  />
                </svg>
                <p className="text-base text-gray-500">
                  No configurations available for this project.
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="w-full lg:w-1/3">
          {hasPrivilege("canEdit") && (
            <div className="">
              <AssignAgentComponent
                agents={Agents}
                assignedAgents={assignedAgents}
                handleAssignAgent={handleAssignAgent}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigDetails;